import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { useCartStore } from '@/stores/cartStore';
import type { CartItem, CartState } from '@/types/cart';

interface CartContextType extends CartState {
  addToCart: (item: Omit<CartItem, 'id' | 'totalPrice'>) => void;
  updateCartItem: (itemId: string, updates: Partial<CartItem>) => void;
  removeFromCart: (itemId: string) => void;
  clearCart: () => void;
  getCartItem: (productId: string, variants: Record<string, string>) => CartItem | undefined;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  // Use Zustand store but expose via Context for backward compatibility
  const store = useCartStore();

  const value: CartContextType = {
    items: store.items,
    totalItems: store.totalItems,
    totalPrice: store.totalPrice,
    addToCart: store.addToCart,
    updateCartItem: store.updateCartItem,
    removeFromCart: store.removeFromCart,
    clearCart: store.clearCart,
    getCartItem: store.getCartItem,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}